import { setCourseList } from '@/lib/slices/course-slice';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { EffectiveRole, role } from 'auth-types';
import { CourseData, CourseEntity, UserCoursesCookie } from 'course-types';
import Request from '@/services/util/request-service';
import toast from 'react-hot-toast';

const AdminCourseAccessService = {
  getEditableAdminCourseList: (cookie?: UserCoursesCookie | null): CourseEntity[] => {
    return cookie?.all?.enterprise ?? [];
  },
  getCourseOwnershipStatus: (course: CourseEntity): 'sole_owner' | 'collaborated' => {
    if (course.ownership_status === 'sole_owner' || course.ownership_status === 'collaborated') {
      return course.ownership_status;
    }

    return 'collaborated';
  },
  getCourseCollaboratorCount: (course: CourseEntity): number => {
    if (
      typeof course.collaborator_count === 'number' &&
      Number.isFinite(course.collaborator_count)
    ) {
      return course.collaborator_count;
    }

    return AdminCourseAccessService.getCourseOwnershipStatus(course) === 'sole_owner' ? 1 : 2;
  },
  getVisibleAdminCourseList: (cookie?: UserCoursesCookie | null): CourseEntity[] => {
    if (!cookie?.all) return [];

    const coursesById = new Map<string, CourseEntity>();

    for (const editableCourse of cookie.all.enterprise ?? []) {
      coursesById.set(String(editableCourse.id), editableCourse);
    }

    for (const groupCourses of Object.values(cookie.all.group ?? {})) {
      for (const groupCourse of groupCourses ?? []) {
        const key = String(groupCourse.id);
        if (!coursesById.has(key)) {
          coursesById.set(key, groupCourse);
        }
      }
    }

    return Array.from(coursesById.values());
  },
  hasEditableCourseAccess: (courseId: string, cookie?: UserCoursesCookie | null): boolean => {
    const accessCookie = cookie ?? UserCourseStorageService.getCourseCookie();
    return Boolean(
      accessCookie?.all?.enterprise?.some((course) => String(course.id) === String(courseId)),
    );
  },
  getEditableAccess: (
    _userRole: role,
    activeCourseData: CourseData,
    effectiveRole?: EffectiveRole | null,
  ): boolean => {
    try {
      if (!activeCourseData?.id) return false;
      if (!effectiveRole) return false;
      if (effectiveRole.roleType !== 'admin') return false;
      if (!effectiveRole.permissions?.perm_course_manage) return false;

      const cookie = UserCourseStorageService.getCourseCookie();
      if (!cookie) return false;

      return AdminCourseAccessService.hasEditableCourseAccess(activeCourseData.id, cookie);
    } catch (error) {
      console.error('Error retrieving edit permissions.');
      return false;
    }
  },
  getGroupCourseList: async (groupId: string): Promise<CourseEntity[]> => {
    let cookie = UserCourseStorageService.getCourseCookie();
    if (!cookie) {
      cookie = await UserCourseStorageService.retrieveCourseCookie();
    }

    try {
      const res = await (await Request.get(`group/c/${groupId}`))?.json();
      const serverCourses: CourseEntity[] = res?.courses ?? [];
      const cachedCourses: CourseEntity[] = cookie?.all?.group?.[groupId] ?? [];

      const equalByIds = (a: CourseEntity[], b: CourseEntity[]) => {
        if (a.length !== b.length) return false;
        const aIds = new Set(a.map((c) => String(c.id)));
        for (const c of b) {
          if (!aIds.has(String(c.id))) return false;
        }
        return true;
      };

      if (!equalByIds(cachedCourses, serverCourses)) {
        if (cookie) {
          if (!cookie.all.group[groupId]) cookie.all.group[groupId] = [];
          cookie.all.group[groupId] = serverCourses;
          UserCourseStorageService.setCourseCookie(cookie);
        }
        return serverCourses;
      }

      return cachedCourses;
    } catch (e) {
      console.error('Error retrieving updated group course list.', e);
      return cookie?.all?.group?.[groupId] ?? [];
    }
  },

  setCourseListToEnterprise: (dispatch: Dispatch<UnknownAction>) => {
    const cookie = UserCourseStorageService.getCourseCookie();
    if (!cookie) return;

    const entry = cookie.all.enterprise ?? [];
    dispatch(setCourseList(entry));
  },
  setCourseListToVisibleAdminCourses: (dispatch: Dispatch<UnknownAction>) => {
    const cookie = UserCourseStorageService.getCourseCookie();
    dispatch(setCourseList(AdminCourseAccessService.getVisibleAdminCourseList(cookie)));
  },
};

export default AdminCourseAccessService;
